/**
 * Content Management → Syllabus Content Library
 *
 * Fresh component. Lists every syllabus submission with its approval status
 * and lets the admin:
 *   • Approve  → AI tutor starts using it (chunks + page images flipped to
 *                status=approved cascaded by the backend).
 *   • Reject   → AI tutor stops using it (soft, auditable — record kept).
 *   • Delete   → hard delete, removes the PDF on disk + all chunks + all
 *                rendered page images.
 *
 * Filters by status (default = pending_review so the admin sees their queue
 * first), plus board / class / subject. Uses the same /api/syllabus/library
 * endpoint as the Admin Command Center, so there's one source of truth.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { apiGet, apiPost, apiDelete, apiPatch, API_BASE, readSessionToken } from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  ShieldCheck, ShieldX, Trash2, RefreshCw, Search, BookOpen,
  CheckCircle2, AlertTriangle, FileText, Info, ExternalLink, Upload, Brush, AlertOctagon, Sparkles,
  Pencil, X, Loader2,
} from 'lucide-react';
import { toast } from 'sonner';

const STATUS_TABS = [
  { key: 'pending_review', label: 'Pending review' },
  { key: 'approved', label: 'Approved' },
  { key: 'rejected', label: 'Rejected' },
  { key: 'all', label: 'All' },
];

const BOARD_FILTER = [
  { value: '', label: 'All boards' },
  { value: 'cbse', label: 'CBSE' },
  { value: 'icse', label: 'ICSE' },
  { value: 'ib', label: 'IB' },
  { value: 'igcse', label: 'Cambridge' },
  { value: 'state_board', label: 'State Board' },
  { value: 'nios', label: 'NIOS' },
];

const CLASS_FILTER = [
  { value: '', label: 'All classes' },
  ...Array.from({ length: 12 }, (_, i) => ({ value: String(i + 1), label: `Class ${i + 1}` })),
];

const FILE_ORIGIN = API_BASE.replace(/\/api$/, '');

// Format ISO date as "23 Apr 2026" — short, no wrap, admin-friendly.
const MONTHS_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
function formatUploadedDate(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso.slice(0, 10);
  return `${d.getDate()} ${MONTHS_SHORT[d.getMonth()]} ${d.getFullYear()}`;
}

// Clean the `{8-hex}_` prefix that the upload handler adds to filenames on
// disk — the admin never needs to see that machine-generated noise.
const prettyName = (s) => (s || '').replace(/^[a-f0-9]{8}_/i, '');

function statusBadge(status) {
  switch (status) {
    case 'approved':
      return <Badge className="bg-emerald-100 text-emerald-700 rounded-full text-[10px] uppercase tracking-wider" data-testid="st-approved">Approved</Badge>;
    case 'rejected':
      return <Badge className="bg-slate-200 text-slate-600 rounded-full text-[10px] uppercase tracking-wider" data-testid="st-rejected">Rejected</Badge>;
    case 'pending_review':
    default:
      return <Badge className="bg-amber-100 text-amber-700 rounded-full text-[10px] uppercase tracking-wider" data-testid="st-pending">Pending review</Badge>;
  }
}

// Transient per-row badge shown only while a bulk action is in flight.
// `pendingStatus` is the status the row is being moved towards (for the "done"
// label) so we don't have to thread the action verb through.
function transientBadge(state, pendingStatus, error) {
  if (state === 'processing') {
    return (
      <Badge className="bg-indigo-100 text-indigo-700 rounded-full text-[10px] uppercase tracking-wider inline-flex items-center gap-1" data-testid="st-processing">
        <RefreshCw className="w-2.5 h-2.5 animate-spin" />
        Processing
      </Badge>
    );
  }
  if (state === 'done') {
    if (pendingStatus === 'deleted') {
      return <Badge className="bg-rose-100 text-rose-700 rounded-full text-[10px] uppercase tracking-wider" data-testid="st-deleted">Deleted</Badge>;
    }
    return statusBadge(pendingStatus);
  }
  if (state === 'failed') {
    // Click-to-open popover instead of a native HTML `title` tooltip.
    // The native tooltip vanishes the moment the cursor moves off the badge
    // — basically impossible to screenshot or read on long errors. This
    // version opens on click and stays open until you click the badge again
    // (or anywhere outside). Auto-positioned above-right so it doesn't get
    // clipped by the table's overflow-hidden.
    return <FailedBadge error={error || 'failed'} />;
  }
  return null;
}

function FailedBadge({ error }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDocClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div ref={ref} className="relative inline-block">
      <Badge
        className="bg-rose-100 text-rose-700 rounded-full text-[10px] uppercase tracking-wider cursor-pointer hover:bg-rose-200 transition-colors"
        data-testid="st-failed"
        onClick={() => setOpen(v => !v)}
        title="Click for error details"
      >
        Failed
      </Badge>
      {open && (
        <div
          className="absolute z-50 top-full mt-1.5 right-0 w-80 bg-white border border-rose-200 rounded-xl shadow-xl p-3"
          data-testid="failed-error-popover"
          onClick={e => e.stopPropagation()}
        >
          <div className="flex items-start justify-between gap-2 mb-2">
            <p className="text-[10px] font-heading uppercase tracking-wider text-rose-700">
              Why this row failed
            </p>
            <button
              onClick={() => setOpen(false)}
              className="text-slate-400 hover:text-slate-700 text-xs leading-none"
              aria-label="Close"
            >
              ✕
            </button>
          </div>
          <p className="text-[11px] font-mono text-slate-700 leading-relaxed break-all whitespace-pre-wrap">
            {error}
          </p>
          <button
            onClick={async () => {
              try { await navigator.clipboard.writeText(error); }
              catch { /* ignored — older browsers */ }
            }}
            className="mt-3 text-[10px] font-heading text-indigo-600 hover:text-indigo-800 hover:underline"
          >
            Copy error
          </button>
        </div>
      )}
    </div>
  );
}

export default function ContentMgmtSyllabusLibrary({ refreshToken }) {
  const [statusTab, setStatusTab] = useState('pending_review');
  const [boardFilter, setBoardFilter] = useState('');
  const [classFilter, setClassFilter] = useState('');
  const [subjectFilter, setSubjectFilter] = useState('');
  const [q, setQ] = useState('');

  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [statusCounts, setStatusCounts] = useState({ pending_review: 0, approved: 0, rejected: 0 });
  const [loading, setLoading] = useState(true);
  const [mutatingId, setMutatingId] = useState(null);  // which row is currently being mutated
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  // Transient per-row status during a bulk action so the admin sees live
  // feedback (Processing → Approved/Rejected/Deleted/Failed) for each file
  // instead of a frozen gray UI until the whole batch finishes. Mirrors the
  // per-file progress UX of the Bulk Syllabus Upload page.
  // Shape: { [submission_id]: { state: 'processing'|'done'|'failed', error?: string } }
  const [rowStates, setRowStates] = useState({});
  // Lightweight overall progress ticker for the bulk bar, e.g. "Approving 3 / 9…"
  const [bulkProgress, setBulkProgress] = useState({ verb: '', done: 0, total: 0 });

  // Pagination — hard cap of 250 per page (backend enforces it too). The
  // admin can pick a smaller page size from the dropdown for snappier
  // loads on slower connections. `page` is 0-indexed.
  const [pageSize, setPageSize] = useState(250);
  const [page, setPage] = useState(0);
  // Per-row metadata edit dialog. Holds the submission being edited or null.
  const [editingRow, setEditingRow] = useState(null);

  const inFlightRef = useRef(null);

  const load = useCallback(async () => {
    // Dedupe: if a load is already in flight, return the same promise so two
    // concurrent useEffects don't fire two requests against the same endpoint.
    // This was the root of the intermittent toast — duplicate calls racing
    // and one of them failing while the other succeeded.
    if (inFlightRef.current) return inFlightRef.current;
    setLoading(true);

    const params = new URLSearchParams();
    if (boardFilter) params.append('board', boardFilter);
    if (classFilter) params.append('class_level', classFilter);
    if (subjectFilter) params.append('subject', subjectFilter);
    if (q) params.append('q', q);
    // Server-side status filter — so the admin can paginate through ALL
    // rows of a single status (e.g. all 670 approved syllabi), not just
    // the current page's items. 'all' means no status filter.
    if (statusTab && statusTab !== 'all') params.append('status', statusTab);
    params.append('limit', String(pageSize));
    params.append('skip', String(page * pageSize));
    const url = `/syllabus/library?${params.toString()}`;

    // Retry-with-backoff: up to 3 attempts, 0 → 700ms → 1500ms. Catches the
    // typical transient hiccup (cold pod warm-up, brief Mongo failover, CDN
    // 502) before bothering the admin with a toast.
    const run = (async () => {
      let lastErr;
      for (let attempt = 0; attempt < 3; attempt++) {
        if (attempt > 0) await new Promise(res => setTimeout(res, attempt === 1 ? 700 : 1500));
        try {
          const r = await apiGet(url);
          setItems(r.items || []);
          setTotal(r.total || 0);
          if (r.status_counts) setStatusCounts(r.status_counts);
          setLoading(false);
          return r;
        } catch (e) {
          lastErr = e;
          // Don't retry on 401/403 — those are auth problems, not transient.
          if (e?.status === 401 || e?.status === 403) break;
        }
      }
      setLoading(false);
      // Surface the real error so we (and the admin) can actually diagnose
      // recurring failures instead of staring at a generic toast.
      const code = lastErr?.status ? `HTTP ${lastErr.status}` : (lastErr?.isNetworkError ? 'network error' : (lastErr?.message || 'unknown error'));
      toast.error(`Failed to load syllabus library (${code})`, {
        description: 'Tap to retry — or check the Network tab for /syllabus/library.',
        action: { label: 'Retry', onClick: () => load() },
        duration: 8000,
      });
      throw lastErr;
    })().finally(() => {
      inFlightRef.current = null;
    });

    inFlightRef.current = run;
    return run.catch(() => {});  // swallow at the boundary — toast already shown
  }, [boardFilter, classFilter, subjectFilter, q, statusTab, pageSize, page]);

  useEffect(() => { load(); }, [load, refreshToken]);

  // Reset to page 0 whenever filters / status tab / page-size change, so
  // the admin doesn't get stranded on page 5 with no rows after filtering.
  useEffect(() => { setPage(0); }, [boardFilter, classFilter, subjectFilter, q, statusTab, pageSize]);

  // Status tab now filters server-side — `items` already contains only the
  // rows matching the active tab. We alias to `filtered` purely so the
  // existing references below (table rendering, selection helpers) don't
  // need to change.
  const filtered = useMemo(() => items, [items]);

  // Per-status totals come from the backend (across all pages, not just
  // the current page). Keeps the tab badges accurate even when there are
  // hundreds of rows across multiple pages.
  const counts = statusCounts;

  const subjectOptions = useMemo(
    () => Array.from(new Set(items.map(i => i.subject).filter(Boolean))).sort(),
    [items],
  );

  const approve = async (sub) => {
    if (!window.confirm(`Approve "${prettyName(sub.file_name)}"?\n\nGurukul Veda will immediately start using this syllabus for all matching student queries (${sub.board?.toUpperCase() || '—'} · Class ${sub.class_level || '—'} · ${sub.subject || '—'}).`)) return;
    setMutatingId(sub.submission_id);
    try {
      await apiPost(`/syllabus/submissions/${sub.submission_id}/approve`, {});
      toast.success('Approved — Gurukul Veda will now use this syllabus');
      setItems(prev => prev.map(x =>
        x.submission_id === sub.submission_id ? { ...x, status: 'approved' } : x
      ));
    } catch (e) {
      toast.error(e?.message === 'UNAUTHORIZED' ? 'Session expired — re-login' : 'Approve failed');
    }
    setMutatingId(null);
  };

  const reject = async (sub) => {
    if (!window.confirm(`Reject "${prettyName(sub.file_name)}"?\n\nThe submission + file stay in the library for audit, but Gurukul Veda will stop reading from it. You can approve later or hard-delete.`)) return;
    setMutatingId(sub.submission_id);
    try {
      await apiPost(`/syllabus/submissions/${sub.submission_id}/reject`, {});
      toast.success('Rejected — Gurukul Veda will no longer use this');
      setItems(prev => prev.map(x =>
        x.submission_id === sub.submission_id ? { ...x, status: 'rejected' } : x
      ));
    } catch (e) {
      toast.error(e?.message === 'UNAUTHORIZED' ? 'Session expired — re-login' : 'Reject failed');
    }
    setMutatingId(null);
  };

  const hardDelete = async (sub) => {
    if (!window.confirm(`Permanently delete "${prettyName(sub.file_name)}"?\n\nRemoves:\n• all rendered page images\n• all RAG chunks\n• the submission record\n• the PDF file on disk\n\nThis action is irreversible.`)) return;
    setMutatingId(sub.submission_id);
    try {
      await apiDelete(`/syllabus/submissions/${sub.submission_id}?delete_file=true`);
      toast.success('Deleted — everything cleaned up');
      setItems(prev => prev.filter(x => x.submission_id !== sub.submission_id));
      setTotal(t => Math.max(0, t - 1));
    } catch (e) {
      toast.error(e?.message === 'UNAUTHORIZED' ? 'Session expired — re-login' : 'Delete failed');
    }
    setMutatingId(null);
  };

  // Force-refresh the quiz + flashcard cache for this chapter's
  // (board, class, subject, topic) tuple. Useful when admin wants fresher
  // LLM output without re-uploading the PDF (e.g. after a prompt tweak).
  // The endpoint also auto-fires whenever a chapter is re-approved with a
  // different content_hash, but this button covers the manual case.
  const refreshCache = async (sub) => {
    const label = `${sub.board?.toUpperCase() || '—'} · Class ${sub.class_level || '—'} · ${sub.subject || '—'}${sub.topic_name ? ' · ' + sub.topic_name : ''}`;
    if (!window.confirm(
      `Refresh cached quiz + flashcards for:\n\n${label}\n\n` +
      `The old cached questions/cards will be cleared and new ones generated ` +
      `in the background (~10–20s). Students hitting this chapter in the meantime ` +
      `may see the cache miss state briefly.`
    )) return;
    setMutatingId(sub.submission_id);
    try {
      const r = await apiPost(`/syllabus/submissions/${sub.submission_id}/refresh-cache`, {});
      toast.success(
        `Cache cleared (quiz: ${r.quiz_cache_cleared}, flashcards: ${r.flashcard_cache_cleared}) — ` +
        `regenerating in background…`,
      );
    } catch (e) {
      toast.error(e?.message === 'UNAUTHORIZED' ? 'Session expired — re-login' : 'Refresh failed');
    }
    setMutatingId(null);
  };

  // ----- BULK ACTIONS (Approve / Reject / Hard-delete N selected) -----
  // Selection survives filtering: we always work off `selectedIds`, never the
  // visible rows. Clearing the tab/filter does NOT auto-clear the selection.
  const toggleRow = (sid) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(sid)) next.delete(sid); else next.add(sid);
      return next;
    });
  };
  const selectAllVisible = () => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      filtered.forEach(r => next.add(r.submission_id));
      return next;
    });
  };
  const clearSelection = () => setSelectedIds(new Set());
  const allVisibleSelected = filtered.length > 0 && filtered.every(r => selectedIds.has(r.submission_id));
  const toggleSelectAllVisible = () => (allVisibleSelected ? clearSelection() : selectAllVisible());

  const runBulkAction = async (action) => {
    const ids = Array.from(selectedIds);
    if (!ids.length) { toast.error('Nothing selected'); return; }
    const verbs = { approve: 'Approve', reject: 'Reject', delete: 'Permanently delete' };
    const verbing = { approve: 'Approving', reject: 'Rejecting', delete: 'Deleting' };
    const verb = verbs[action];
    let msg = `${verb} ${ids.length} selected submission${ids.length === 1 ? '' : 's'}?`;
    if (action === 'delete') {
      msg += '\n\nHARD DELETE — removes PDFs, RAG chunks, page images. This action is irreversible.';
    } else if (action === 'approve') {
      msg += '\n\nGurukul Veda will immediately start using these for student responses.';
    } else {
      msg += '\n\nGurukul Veda will stop using these. Records stay for audit; you can hard-delete later.';
    }
    if (!window.confirm(msg)) return;

    setBulkBusy(true);
    // Seed every selected row with "processing" so the admin sees per-file
    // status immediately (same UX pattern as Bulk Syllabus Upload). Each row
    // then transitions to done/failed individually as its server call finishes.
    setRowStates(() => Object.fromEntries(ids.map(id => [id, { state: 'processing' }])));
    setBulkProgress({ verb: verbing[action], done: 0, total: ids.length });

    // SINGLE BULK CALL.
    // Previously we looped one-id-at-a-time for per-row live feedback. That
    // pattern looked nice but cost reliability: with 27 chapters selected
    // and Cloudflare in front, 27 sequential round-trips meant ANY single
    // hiccup forced the admin to retry. Real-world failure rate at peak
    // load: 3-10 attempts to clear a typical batch.
    //
    // The backend's `/syllabus/submissions/bulk-action` endpoint is built
    // for true bulk operations (3-4 mongo round trips for the whole batch
    // regardless of size). We now send every id in ONE request and update
    // the row states optimistically from the server's succeeded/failed
    // lists — visually the experience is the same (each row flips from
    // 'processing' to 'done'/'failed') but it's 27× more reliable.
    const succeededAll = [];
    const failedAll = [];
    let okCount = 0;
    let failCount = 0;
    try {
      const r = await apiPost('/syllabus/submissions/bulk-action', {
        submission_ids: ids, action, delete_file: true,
      });
      const okSet = new Set(r.succeeded || []);
      const failMap = new Map((r.failed || []).map(f => [f.submission_id, f.error || 'failed']));
      for (const sid of ids) {
        if (okSet.has(sid)) {
          okCount += 1;
          succeededAll.push(sid);
          if (action === 'delete') {
            setItems(prev => prev.filter(x => x.submission_id !== sid));
            setTotal(t => Math.max(0, t - 1));
          } else {
            const newStatus = action === 'approve' ? 'approved' : 'rejected';
            setItems(prev => prev.map(x =>
              x.submission_id === sid ? { ...x, status: newStatus } : x
            ));
          }
          setRowStates(prev => ({ ...prev, [sid]: { state: 'done' } }));
        } else {
          failCount += 1;
          failedAll.push(sid);
          setRowStates(prev => ({ ...prev, [sid]: { state: 'failed', error: failMap.get(sid) || 'failed' } }));
        }
      }
      setBulkProgress(p => ({ ...p, done: ids.length }));
    } catch (e) {
      // The whole bulk request failed (network / 5xx / 520). Mark every row
      // as failed so the admin can see which batch needs a retry — they're
      // still selected (see setSelectedIds below), so Approve can be clicked
      // again with one click.
      const why = e?.message === 'UNAUTHORIZED' ? 'session expired' : (e?.message || 'failed');
      for (const sid of ids) {
        failCount += 1;
        failedAll.push(sid);
        setRowStates(prev => ({ ...prev, [sid]: { state: 'failed', error: why } }));
      }
    }

    // Drop only the succeeded rows from selection; keep failed ones so the
    // admin can retry just those instead of re-selecting from scratch.
    setSelectedIds(prev => {
      const next = new Set(prev);
      succeededAll.forEach(id => next.delete(id));
      return next;
    });

    if (failCount === 0) toast.success(`${verb}d ${okCount} submission${okCount === 1 ? '' : 's'}`);
    else if (okCount === 0) {
      // Surface the actual error reason — without this, debugging recurring
      // failures becomes guesswork. The reason comes from server's
      // `failed[*].error` or the catch-block's `e.message` (HTTP status,
      // network error, etc.).
      const reasons = [...new Set(ids.map(sid => rowStates[sid]?.error || '?').filter(Boolean))];
      const why = reasons.length === 1 ? reasons[0] : reasons.slice(0, 3).join(' / ');
      toast.error(`All ${failCount} failed — ${why}`, {
        duration: 12000,
        description: 'Hover any FAILED badge to see its specific error. Items stay selected for retry.',
      });
    }
    else toast.warning(`${verb}d ${okCount}/${okCount + failCount} — ${failCount} failed (kept selected)`);

    // Keep the "done" / "failed" labels visible for a moment so the admin sees
    // the final outcome per row, then fade back to the canonical statusBadge.
    setBulkBusy(false);
    setBulkProgress({ verb: '', done: 0, total: 0 });
    setTimeout(() => setRowStates({}), 2500);
  };

  // ----- WIPE ALL — nuclear option for a polluted library -----
  const [wiping, setWiping] = useState(false);
  const wipeAll = async () => {
    const phrase = window.prompt(
      'WIPE EVERY syllabus submission, chunk, page-image, PDF on disk, and Mongo backup?\n\n' +
      'This is irreversible. Type WIPE to confirm.',
    );
    if (phrase !== 'WIPE') {
      if (phrase !== null) toast.error('Phrase did not match — cancelled.');
      return;
    }
    setWiping(true);
    try {
      const r = await apiPost('/syllabus/submissions/wipe-all?confirm=YES_WIPE_EVERYTHING', {});
      toast.success(
        `Wiped: ${r.submissions_deleted} rows · ${r.chunks_deleted} chunks · ${r.page_images_deleted} page images · ${r.files_deleted} files`,
      );
      setItems([]); setTotal(0); clearSelection();
    } catch (e) {
      toast.error(e?.message === 'UNAUTHORIZED' ? 'Session expired — re-login' : 'Wipe failed');
    }
    setWiping(false);
  };

  // ----- Re-upload (heal a row whose PDF is gone from disk + Mongo backup) -----
  const fileInputRef = useRef(null);
  const reuploadTargetRef = useRef(null);

  const onPickReupload = (sub) => {
    reuploadTargetRef.current = sub;
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
      fileInputRef.current.click();
    }
  };

  const onReuploadFile = async (e) => {
    const file = e.target.files?.[0];
    const sub = reuploadTargetRef.current;
    reuploadTargetRef.current = null;
    if (!file || !sub) return;
    // 300 MB cap matches the backend `/replace-file` ceiling. For files
    // larger than this, the admin should split the chapter PDF and use
    // the bulk-upload page (which routes >50 MB through chunked upload).
    if (file.size > 300 * 1024 * 1024) {
      const mb = (file.size / 1024 / 1024).toFixed(1);
      toast.error(`File too large (${mb} MB). Max 300 MB. Split very large textbooks per-chapter.`);
      return;
    }
    setMutatingId(sub.submission_id);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const tok = readSessionToken();
      const res = await fetch(`${API_BASE}/syllabus/submissions/${sub.submission_id}/replace-file`, {
        method: 'POST',
        credentials: 'include',
        headers: tok ? { Authorization: `Bearer ${tok}` } : {},
        body: fd,
      });
      if (!res.ok) {
        let detail = '';
        try {
          const body = await res.text();
          try {
            const j = JSON.parse(body);
            if (j?.detail && typeof j.detail === 'string') detail = j.detail;
          } catch {
            if (body && body.length < 200) detail = body;
          }
        } catch { /* ignore */ }
        throw new Error(detail || `Re-upload failed (${res.status})`);
      }
      const data = await res.json();
      toast.success(`Re-uploaded — file restored (${data.size_mb} MB). RAG chunks rebuilt.`);
      setItems(prev => prev.map(x =>
        x.submission_id === sub.submission_id
          ? { ...x, on_disk: true, in_backup: true, size_mb: data.size_mb, stored_file_name: data.stored_file_name, pdf_url: data.pdf_url }
          : x,
      ));
    } catch (err) {
      toast.error(err?.message || 'Re-upload failed');
    }
    setMutatingId(null);
  };

  // ----- Sweep orphans (bulk-delete ghost rows whose file is truly lost) -----
  const [sweeping, setSweeping] = useState(false);
  const sweepOrphans = async () => {
    setSweeping(true);
    try {
      // Step 1: dry-run to show admin EXACTLY what would be deleted.
      const preview = await apiPost('/syllabus/submissions/cleanup-orphans?dry_run=true', {});
      if (preview.orphans_found === 0) {
        toast.success('No orphans found — every row has its file 🎉');
        setSweeping(false);
        return;
      }
      const sample = (preview.manifest || []).slice(0, 5)
        .map(o => `• ${o.file_name || o.submission_id}`).join('\n');
      const more = preview.orphans_found > 5 ? `\n…and ${preview.orphans_found - 5} more` : '';
      const ok = window.confirm(
        `Found ${preview.orphans_found} orphan row${preview.orphans_found === 1 ? '' : 's'} (${preview.kept} healthy rows will be kept).\n\nWill DELETE:\n${sample}${more}\n\nThis action is irreversible. Proceed?`,
      );
      if (!ok) {
        setSweeping(false);
        return;
      }
      // Step 2: real sweep
      const r = await apiPost('/syllabus/submissions/cleanup-orphans', {});
      toast.success(`Swept ${r.orphans_found} orphan row${r.orphans_found === 1 ? '' : 's'} (${r.kept} kept)`);
      await load();
    } catch (e) {
      toast.error(e?.message === 'UNAUTHORIZED' ? 'Session expired — re-login' : 'Sweep failed');
    }
    setSweeping(false);
  };

  return (
    <div className="space-y-4 min-w-0 max-w-full" data-testid="cm-syllabus-library">
      <div className="flex flex-wrap items-start gap-2">
        <BookOpen className="w-5 h-5 text-indigo-500 mt-0.5" />
        <div className="min-w-0 flex-1 basis-48">
          <h3 className="font-heading text-base font-semibold text-slate-800">Syllabus Content Library</h3>
          <p className="text-[11px] text-slate-500 font-body mt-0.5">
            Every syllabus ingested into Gurukul Veda. Only <strong>approved</strong> submissions are read by Gurukul Veda for student responses. Use this gate to vet uploads before they reach students.
          </p>
        </div>
        <Button onClick={wipeAll} disabled={wiping || items.length === 0}
          variant="ghost"
          className="ml-auto text-xs font-heading text-rose-700 hover:bg-rose-50 disabled:opacity-40"
          data-testid="cm-library-wipe-all"
          title="Permanently delete EVERY syllabus submission + PDF + chunks + page images. Irreversible.">
          <AlertOctagon className={`w-3.5 h-3.5 mr-1.5 ${wiping ? 'animate-pulse' : ''}`} />
          {wiping ? 'Wiping…' : 'Wipe all'}
        </Button>
        <Button onClick={sweepOrphans} disabled={sweeping}
          variant="ghost"
          className="text-xs font-heading text-rose-600 hover:bg-rose-50"
          data-testid="cm-library-sweep-orphans"
          title="Permanently delete every row whose PDF is missing from both disk + Mongo backup">
          <Brush className={`w-3.5 h-3.5 mr-1.5 ${sweeping ? 'animate-pulse' : ''}`} />
          {sweeping ? 'Sweeping…' : 'Sweep orphans'}
        </Button>
        <Button onClick={load} disabled={loading}
          variant="ghost"
          className="text-xs font-heading text-slate-600"
          data-testid="cm-library-refresh">
          <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </Button>
      </div>

      {/* Hidden file input for the per-row Re-upload action */}
      <input
        ref={fileInputRef}
        type="file"
        accept="application/pdf,.pdf"
        className="hidden"
        onChange={onReuploadFile}
        data-testid="cm-library-reupload-input"
      />

      {/* Status tabs */}
      <div className="flex flex-wrap gap-2">
        {STATUS_TABS.map(t => {
          // 'all' = sum of every status count; other tabs use the matching
          // status total from the server (works across all pages).
          const allCount = (counts.pending_review || 0) + (counts.approved || 0) + (counts.rejected || 0);
          const n = t.key === 'all' ? allCount : (counts[t.key] || 0);
          const active = statusTab === t.key;
          return (
            <button key={t.key} onClick={() => setStatusTab(t.key)}
              data-testid={`cm-library-tab-${t.key}`}
              className={`px-3 py-1.5 rounded-full text-xs font-heading transition-all ${active ? 'bg-slate-900 text-white' : 'bg-white border border-slate-200 text-slate-600 hover:border-slate-300'}`}>
              {t.label}
              <span className={`ml-1.5 inline-block min-w-[20px] text-center text-[10px] rounded-full px-1.5 py-0.5 ${active ? 'bg-white/20' : 'bg-slate-100'}`}>
                {n}
              </span>
            </button>
          );
        })}
      </div>

      {/* Filters */}
      <Card className="p-3 rounded-2xl border-slate-100" data-testid="cm-library-filters">
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
            <Input value={q} onChange={e => setQ(e.target.value)}
              placeholder="Search by filename or chapter…"
              className="pl-8 text-xs h-9 rounded-full border-slate-200"
              data-testid="cm-library-search" />
          </div>
          <select value={boardFilter} onChange={e => setBoardFilter(e.target.value)}
            data-testid="cm-library-board"
            className="text-xs rounded-full border border-slate-200 bg-white px-3 h-9 font-heading">
            {BOARD_FILTER.map(b => <option key={b.value} value={b.value}>{b.label}</option>)}
          </select>
          <select value={classFilter} onChange={e => setClassFilter(e.target.value)}
            data-testid="cm-library-class"
            className="text-xs rounded-full border border-slate-200 bg-white px-3 h-9 font-heading">
            {CLASS_FILTER.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
          </select>
          {subjectOptions.length > 0 && (
            <select value={subjectFilter} onChange={e => setSubjectFilter(e.target.value)}
              data-testid="cm-library-subject"
              className="text-xs rounded-full border border-slate-200 bg-white px-3 h-9 font-heading">
              <option value="">All subjects</option>
              {subjectOptions.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          )}
          {(q || boardFilter || classFilter || subjectFilter) && (
            <button onClick={() => { setQ(''); setBoardFilter(''); setClassFilter(''); setSubjectFilter(''); }}
              className="text-[11px] text-slate-500 hover:text-slate-800 underline"
              data-testid="cm-library-clear">
              Clear
            </button>
          )}
        </div>
      </Card>

      {/* Bulk action bar — appears only when rows are selected */}
      {selectedIds.size > 0 && (
        <div className="flex flex-wrap items-center gap-2 px-4 py-2.5 rounded-2xl bg-indigo-50 border border-indigo-100"
          data-testid="cm-library-bulk-bar">
          <span className="text-xs font-heading text-indigo-800">
            {selectedIds.size} selected
          </span>
          <button onClick={clearSelection} disabled={bulkBusy}
            className="text-[11px] text-slate-500 hover:text-slate-800 underline disabled:opacity-40"
            data-testid="cm-library-bulk-clear">
            Clear
          </button>
          {bulkBusy && bulkProgress.total > 0 && (
            <span className="inline-flex items-center gap-1.5 text-[11px] font-body text-indigo-700"
              data-testid="cm-library-bulk-progress">
              <RefreshCw className="w-3 h-3 animate-spin" />
              {bulkProgress.verb} {bulkProgress.done} / {bulkProgress.total}…
            </span>
          )}
          <div className="ml-auto flex flex-wrap items-center gap-1.5">
            <Button onClick={() => runBulkAction('approve')} disabled={bulkBusy}
              data-testid="cm-library-bulk-approve"
              className="bg-emerald-500 hover:bg-emerald-600 text-white rounded-full text-xs font-heading disabled:opacity-40">
              <ShieldCheck className="w-3.5 h-3.5 mr-1.5" /> Approve {selectedIds.size}
            </Button>
            <Button onClick={() => runBulkAction('reject')} disabled={bulkBusy}
              data-testid="cm-library-bulk-reject"
              className="bg-amber-500 hover:bg-amber-600 text-white rounded-full text-xs font-heading disabled:opacity-40">
              <ShieldX className="w-3.5 h-3.5 mr-1.5" /> Reject {selectedIds.size}
            </Button>
            <Button onClick={() => runBulkAction('delete')} disabled={bulkBusy}
              data-testid="cm-library-bulk-delete"
              className="bg-rose-500 hover:bg-rose-600 text-white rounded-full text-xs font-heading disabled:opacity-40">
              <Trash2 className="w-3.5 h-3.5 mr-1.5" /> Delete {selectedIds.size}
            </Button>
          </div>
        </div>
      )}
      <Card className="p-0 rounded-2xl border-slate-100 overflow-hidden" data-testid="cm-library-table-card">
        {loading && items.length === 0 ? (
          <div className="py-16 flex items-center justify-center">
            <RefreshCw className="w-5 h-5 text-indigo-500 animate-spin" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="py-16 text-center">
            <FileText className="w-9 h-9 mx-auto text-slate-300 mb-2" />
            <p className="text-xs text-slate-500 font-body">
              {statusTab === 'pending_review'
                ? 'Nothing pending review. Upload PDFs from the "Bulk Syllabus Upload" tab above.'
                : 'No submissions match these filters.'}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs table-fixed">
              <colgroup>
                <col className="w-[3%]" />
                <col className="w-[28%]" />
                <col className="w-[15%]" />
                <col className="w-[15%]" />
                <col className="w-[4%]" />
                <col className="w-[5%]" />
                <col className="w-[9%]" />
                <col className="w-[8%]" />
                <col className="w-[13%]" />
              </colgroup>
              <thead className="bg-slate-50/70 text-slate-500 text-[10px] uppercase tracking-wider">
                <tr>
                  <th className="text-center px-2 py-2">
                    <input
                      type="checkbox"
                      checked={allVisibleSelected}
                      onChange={toggleSelectAllVisible}
                      disabled={bulkBusy || filtered.length === 0}
                      data-testid="cm-library-select-all"
                      className="rounded border-slate-300 cursor-pointer"
                      title={allVisibleSelected ? 'Clear selection' : 'Select all visible rows'}
                    />
                  </th>
                  <th className="text-left px-3 py-2">File</th>
                  <th className="text-left px-3 py-2">Scope</th>
                  <th className="text-left px-3 py-2">Chapter</th>
                  <th className="text-right px-3 py-2">Pages</th>
                  <th className="text-right px-3 py-2">Chunks</th>
                  <th className="text-center px-3 py-2">Status</th>
                  <th className="text-center px-3 py-2">Uploaded</th>
                  <th className="text-center px-3 py-2">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((s, i) => {
                  const st = s.status || 'pending_review';
                  const busy = mutatingId === s.submission_id;
                  return (
                    <tr key={s.submission_id}
                      className={`border-t border-slate-100 hover:bg-slate-50/40 align-middle ${selectedIds.has(s.submission_id) ? 'bg-indigo-50/40' : ''}`}
                      data-testid={`cm-library-row-${i}`}>
                      <td className="px-2 py-2.5 text-center">
                        <input
                          type="checkbox"
                          checked={selectedIds.has(s.submission_id)}
                          onChange={() => toggleRow(s.submission_id)}
                          disabled={bulkBusy || busy}
                          data-testid={`cm-library-row-select-${i}`}
                          className="rounded border-slate-300 cursor-pointer"
                        />
                      </td>
                      <td className="px-3 py-2.5">
                        <div className="flex items-start gap-2 min-w-0">
                          <FileText className="w-3.5 h-3.5 text-indigo-400 mt-0.5 shrink-0" />
                          <div className="min-w-0 flex-1">
                            <p className="font-body text-slate-800 truncate leading-snug" title={prettyName(s.file_name)}>{prettyName(s.file_name) || '—'}</p>
                            <div className="flex items-center gap-1.5 mt-0.5 text-[10px] text-slate-400 font-body">
                              <span>{s.size_mb ? `${s.size_mb.toFixed(1)} MB` : '—'}</span>
                              {!s.on_disk && (
                                <span
                                  className="text-rose-500 inline-flex items-center gap-0.5"
                                  title={
                                    "Original PDF was lost from BOTH pod-local disk AND the Mongo backup tier, " +
                                    "AND no rendered chunks/images survive either. The chapter is genuinely broken " +
                                    "and the AI tutor cannot answer questions on it. Re-upload the PDF to rebuild."
                                  }
                                >
                                  <AlertTriangle className="w-2.5 h-2.5" />missing file
                                </span>
                              )}
                              {/* The "source archived" hint was removed —
                                  it varied unpredictably depending on which
                                  Kubernetes pod served the request (each pod
                                  has its own ephemeral local disk), so the
                                  same chapter could show "source archived"
                                  on one page load and nothing on the next.
                                  That inconsistency was confusing without
                                  conveying any actionable info — the
                                  `source_blob_present` field is still in
                                  the API response for ops/debugging. */}
                              {s.render_note && <span className="text-amber-600 inline-flex items-center gap-0.5" title={s.render_note}><AlertTriangle className="w-2.5 h-2.5" />text-only</span>}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-3 py-2.5">
                        <p className="font-body text-slate-700 leading-snug truncate" title={`${(s.board || '').toUpperCase()} · Class ${s.class_level || '?'} · ${s.subject || '?'}`}>
                          <span className="font-heading text-indigo-600 uppercase tracking-wide text-[10px]">{s.board || '—'}</span>
                          <span className="text-slate-300 mx-1.5">·</span>
                          <span>Class {s.class_level || '—'}</span>
                        </p>
                        <p className="text-[10px] text-emerald-700 font-body truncate mt-0.5" title={s.subject}>
                          {s.subject || '—'}
                          {s.new_subject_pending_review && (
                            <span
                              className="ml-1.5 inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-800 text-[9px] font-heading uppercase tracking-wider"
                              title={
                                "This subject spelling didn't match any existing approved subject in this " +
                                "board+class. Review before approving — it may be a new subject, or a near-duplicate " +
                                "of an existing one (use Edit ✎ to retag, or merge from Content Tree)."
                              }
                              data-testid={`cm-library-new-subject-${s.submission_id}`}>
                              <AlertTriangle className="w-2.5 h-2.5" /> new
                            </span>
                          )}
                        </p>
                      </td>
                      <td className="px-3 py-2.5 font-body text-slate-700">
                        <p className="truncate" title={s.topic_name}>{s.topic_name || <span className="text-slate-400 italic">—</span>}</p>
                      </td>
                      <td className="px-3 py-2.5 text-right text-slate-600 font-body">{s.pages_rendered ?? '—'}</td>
                      <td className="px-3 py-2.5 text-right text-slate-600 font-body">{s.chunks_extracted ?? '—'}</td>
                      <td className="px-3 py-2.5 text-center">{(() => {
                        const rs = rowStates[s.submission_id];
                        if (rs) return transientBadge(rs.state, st, rs.error);
                        return statusBadge(st);
                      })()}</td>
                      <td className="px-3 py-2.5 text-center text-[10px] text-slate-500 font-body whitespace-nowrap">
                        {formatUploadedDate(s.created_at)}
                      </td>
                      <td className="px-3 py-2.5 text-center">
                        <div className="flex items-center justify-center gap-1.5 whitespace-nowrap">
                          {s.on_disk && (
                            <a href={`${FILE_ORIGIN}${s.pdf_url}`} target="_blank" rel="noreferrer"
                              className="p-1.5 rounded-md text-indigo-500 hover:bg-indigo-50 hover:text-indigo-700"
                              data-testid={`cm-library-view-${i}`} title="Open PDF">
                              <ExternalLink className="w-3.5 h-3.5" />
                            </a>
                          )}
                          {!s.on_disk && (
                            <button
                              onClick={() => onPickReupload(s)}
                              disabled={busy}
                              data-testid={`cm-library-reupload-${i}`}
                              className="p-1.5 rounded-md text-blue-600 hover:bg-blue-50 hover:text-blue-700 disabled:opacity-40"
                              title="Re-upload PDF — restores the file + rebuilds RAG chunks">
                              {busy ? <span className="text-[10px]">…</span> : <Upload className="w-3.5 h-3.5" />}
                            </button>
                          )}
                          {st !== 'approved' && (
                            <button
                              onClick={() => approve(s)}
                              disabled={busy}
                              data-testid={`cm-library-approve-${i}`}
                              className="p-1.5 rounded-md text-emerald-600 hover:bg-emerald-50 hover:text-emerald-700 disabled:opacity-40"
                              title="Approve — AI starts using this">
                              {busy ? <span className="text-[10px]">…</span> : <ShieldCheck className="w-3.5 h-3.5" />}
                            </button>
                          )}
                          {st !== 'rejected' && (
                            <button
                              onClick={() => reject(s)}
                              disabled={busy}
                              data-testid={`cm-library-reject-${i}`}
                              className="p-1.5 rounded-md text-amber-600 hover:bg-amber-50 hover:text-amber-700 disabled:opacity-40"
                              title="Reject — AI stops using this">
                              {busy ? <span className="text-[10px]">…</span> : <ShieldX className="w-3.5 h-3.5" />}
                            </button>
                          )}
                          {st === 'approved' && (
                            <button
                              onClick={() => refreshCache(s)}
                              disabled={busy}
                              data-testid={`cm-library-refresh-cache-${i}`}
                              className="p-1.5 rounded-md text-indigo-600 hover:bg-indigo-50 hover:text-indigo-700 disabled:opacity-40"
                              title="Clear cached quiz + flashcards for this chapter and regenerate from latest LLM">
                              {busy ? <span className="text-[10px]">…</span> : <Sparkles className="w-3.5 h-3.5" />}
                            </button>
                          )}
                          <button
                            onClick={() => setEditingRow(s)}
                            disabled={busy}
                            data-testid={`cm-library-edit-${i}`}
                            className="p-1.5 rounded-md text-slate-500 hover:bg-slate-100 hover:text-slate-700 disabled:opacity-40"
                            title="Edit board / class / subject / topic (cascades to chunks, page images, content library, and caches)">
                            {busy ? <span className="text-[10px]">…</span> : <Pencil className="w-3.5 h-3.5" />}
                          </button>
                          <button
                            onClick={() => hardDelete(s)}
                            disabled={busy}
                            data-testid={`cm-library-delete-${i}`}
                            className="p-1.5 rounded-md text-rose-500 hover:bg-rose-50 hover:text-rose-700 disabled:opacity-40"
                            title="Permanently delete (PDF + chunks + page images)">
                            {busy ? <span className="text-[10px]">…</span> : <Trash2 className="w-3.5 h-3.5" />}
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        <div className="px-4 py-2 bg-slate-50/60 border-t border-slate-100 flex flex-wrap items-center gap-x-4 gap-y-1 text-[10px] text-slate-500 font-body">
          <span className="inline-flex items-center gap-1"><Info className="w-3 h-3" />
            {(() => {
              // Production-accurate row math: backend returns up to `pageSize`
              // rows from the *server-side* paginated query, and the FE
              // additionally hides rows that don't match the active status tab.
              const startRow = total === 0 ? 0 : page * pageSize + 1;
              const endRow = Math.min(total, (page + 1) * pageSize);
              const visibleAfterTabFilter = filtered.length;
              return (
                <span data-testid="cm-library-pagination-summary">
                  Showing rows <strong>{startRow}–{endRow}</strong> of <strong>{total}</strong>
                  {visibleAfterTabFilter !== items.length && (
                    <span className="text-slate-400"> · {visibleAfterTabFilter} match current tab</span>
                  )}
                </span>
              );
            })()}
          </span>
          {/* Pagination controls — hard-capped at 250/page server-side. */}
          <div className="inline-flex flex-wrap items-center gap-2 ml-auto max-w-full" data-testid="cm-library-pagination">
            <label className="inline-flex items-center gap-1 text-slate-500">
              Per page
              <select
                value={pageSize}
                onChange={e => setPageSize(parseInt(e.target.value, 10) || 250)}
                data-testid="cm-library-page-size"
                disabled={loading}
                className="border border-slate-200 rounded-md px-1.5 py-0.5 text-[10px] bg-white hover:border-indigo-300 focus:outline-none focus:ring-1 focus:ring-indigo-300">
                <option value="50">50</option>
                <option value="100">100</option>
                <option value="250">250</option>
              </select>
            </label>
            <button
              onClick={() => setPage(p => Math.max(0, p - 1))}
              disabled={page === 0 || loading}
              data-testid="cm-library-prev-page"
              className="px-2 py-0.5 rounded-md border border-slate-200 bg-white text-slate-600 hover:border-indigo-300 disabled:opacity-30 disabled:cursor-not-allowed">
              ← Prev
            </button>
            <span className="text-slate-500">
              Page <strong>{page + 1}</strong> of <strong>{Math.max(1, Math.ceil(total / pageSize))}</strong>
            </span>
            <button
              onClick={() => setPage(p => p + 1)}
              disabled={(page + 1) * pageSize >= total || loading}
              data-testid="cm-library-next-page"
              className="px-2 py-0.5 rounded-md border border-slate-200 bg-white text-slate-600 hover:border-indigo-300 disabled:opacity-30 disabled:cursor-not-allowed">
              Next →
            </button>
          </div>
        </div>
        <div className="px-4 py-2 bg-slate-50/60 border-t border-slate-100 flex flex-wrap items-center gap-x-4 gap-y-1 text-[10px] text-slate-500 font-body">
          <span className="inline-flex items-center gap-1" title="Open PDF"><ExternalLink className="w-3 h-3 text-indigo-500" /> View</span>
          <span className="inline-flex items-center gap-1" title="Re-upload missing PDF"><Upload className="w-3 h-3 text-blue-600" /> Re-upload</span>
          <span className="inline-flex items-center gap-1" title="Approve"><ShieldCheck className="w-3 h-3 text-emerald-600" /> Approve</span>
          <span className="inline-flex items-center gap-1" title="Reject"><ShieldX className="w-3 h-3 text-amber-600" /> Reject</span>
          <span className="inline-flex items-center gap-1" title="Edit metadata"><Pencil className="w-3 h-3 text-slate-500" /> Edit</span>
          <span className="inline-flex items-center gap-1" title="Delete"><Trash2 className="w-3 h-3 text-rose-500" /> Delete</span>
          <span className="text-slate-300">|</span>
          <span className="text-emerald-700">Approved items are live for students immediately.</span>
        </div>
      </Card>
      {editingRow && (
        <EditMetadataDialog
          row={editingRow}
          onClose={() => setEditingRow(null)}
          onSaved={(applied) => {
            // Locally patch the row so the table reflects the new fields
            // immediately, then reload in background to pull any cascade
            // counts / canonicalised subject the backend resolved.
            setItems(prev => prev.map(r =>
              r.submission_id === editingRow.submission_id
                ? { ...r, ...applied }
                : r));
            setEditingRow(null);
            load();
          }}
        />
      )}
    </div>
  );
}


/**
 * EditMetadataDialog
 *
 * Lets an admin retag a syllabus submission's board / class / subject /
 * topic — works on both pre- and post-approval rows. Subject input is
 * soft-normalised server-side against the (new) board+class scope so
 * "Health & Physical Education" rolls into whatever canonical spelling
 * already exists in that class. The PATCH cascades to chunks + page
 * images + content_library + caches in one round-trip.
 */
function EditMetadataDialog({ row, onClose, onSaved }) {
  const [board, setBoard] = useState(row?.board || '');
  const [classLevel, setClassLevel] = useState(row?.class_level || '');
  const [subject, setSubject] = useState(row?.subject || '');
  const [topicName, setTopicName] = useState(row?.topic_name || '');
  const [busy, setBusy] = useState(false);

  const dirty = board !== (row?.board || '')
    || classLevel !== (row?.class_level || '')
    || subject !== (row?.subject || '')
    || topicName !== (row?.topic_name || '');

  const submit = async () => {
    if (!dirty) {
      onClose();
      return;
    }
    setBusy(true);
    try {
      const r = await apiPatch(`/syllabus/submissions/${row.submission_id}/metadata`, {
        board, class_level: classLevel, subject, topic_name: topicName,
      });
      const cascadeTotal = Object.values(r?.cascade || {}).reduce((a, b) => a + (b > 0 ? b : 0), 0);
      toast.success(
        cascadeTotal > 0
          ? `Saved — cascaded across ${cascadeTotal} linked row${cascadeTotal === 1 ? '' : 's'}.`
          : 'Saved.',
      );
      onSaved?.(r?.applied || {});
    } catch (e) {
      toast.error(e?.message || 'Failed to save metadata');
    }
    setBusy(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 backdrop-blur-sm p-4"
      data-testid="edit-metadata-dialog">
      <Card className="w-full max-w-md p-5 rounded-2xl space-y-4">
        <div className="flex items-start gap-2">
          <Pencil className="w-5 h-5 text-indigo-500 mt-0.5" />
          <div className="flex-1">
            <h3 className="font-heading text-base font-semibold text-slate-800">Edit submission metadata</h3>
            <p className="text-[11px] text-slate-500 font-body mt-0.5 truncate">
              {prettyName(row?.file_name)}
            </p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700"
            data-testid="edit-metadata-close" aria-label="Close">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1">
            <label className="text-[10px] uppercase tracking-wider font-heading text-slate-500">Board</label>
            <Input value={board} onChange={e => setBoard(e.target.value.toLowerCase().trim())}
              placeholder="cbse" className="text-xs h-9 rounded-md border-slate-200"
              data-testid="edit-metadata-board" />
          </div>
          <div className="space-y-1">
            <label className="text-[10px] uppercase tracking-wider font-heading text-slate-500">Class</label>
            <Input value={classLevel} onChange={e => setClassLevel(e.target.value.replace(/[^0-9]/g, ''))}
              placeholder="10" className="text-xs h-9 rounded-md border-slate-200"
              data-testid="edit-metadata-class" />
          </div>
        </div>

        <div className="space-y-1">
          <label className="text-[10px] uppercase tracking-wider font-heading text-slate-500">Subject</label>
          <Input value={subject} onChange={e => setSubject(e.target.value)}
            placeholder="Health and Physical Education"
            className="text-xs h-9 rounded-md border-slate-200"
            data-testid="edit-metadata-subject" />
          <p className="text-[10px] text-slate-400 font-body">
            Tip: spelling is fuzzy-matched against existing approved subjects in this class. A close match (e.g. &ldquo;Health &amp; Physical Education&rdquo;) is auto-rolled into the existing canonical spelling.
          </p>
        </div>

        <div className="space-y-1">
          <label className="text-[10px] uppercase tracking-wider font-heading text-slate-500">Chapter / topic</label>
          <Input value={topicName} onChange={e => setTopicName(e.target.value)}
            placeholder="Yoga for Healthy Living"
            className="text-xs h-9 rounded-md border-slate-200"
            data-testid="edit-metadata-topic" />
        </div>

        <div className="flex items-center justify-end gap-2 pt-2 border-t border-slate-100">
          <Button variant="ghost" onClick={onClose} disabled={busy}
            className="text-xs font-heading text-slate-600"
            data-testid="edit-metadata-cancel">Cancel</Button>
          <Button onClick={submit} disabled={busy || !dirty}
            className="bg-indigo-600 hover:bg-indigo-700 text-white rounded-full text-xs font-heading disabled:opacity-40"
            data-testid="edit-metadata-save">
            {busy
              ? <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> Saving…</>
              : 'Save metadata'
            }
          </Button>
        </div>
      </Card>
    </div>
  );
}

